import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable, switchMap, tap } from 'rxjs';
import { EventResponseEvent } from '../../types/event-response.type';
import { EventsHttpService } from '../../services/events.http.service';
import { SearchQueryParams } from '../../types/ui.type';
import { startWith } from 'rxjs/operators';
import { LocalStorageService } from '../../services/local-storage.service';
import { ResizeService } from '../../services/resize.service';
import { AuthService } from '../../services/auth.service';
import { Organization } from '../../types/organization.type';
import { BREAKPOINT_MD } from '../../constants/constants';
import { ICONS } from '../../constants/constants';

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultsComponent implements OnInit {
  private innerWidth: number = window.innerWidth;
  public selectedEvent: EventResponseEvent | null = null;
  public isMobile$ = this.resizeService.isMobile$;
  private userId: number | undefined;
  public organizations: Organization[];
  public organization: Organization | undefined;
  public isLoadingOrganizationEvents: boolean = false;
  protected readonly ICONS = ICONS;

  public events$: Observable<EventResponseEvent[] | null> =
    this.route.queryParams.pipe(
      switchMap((queryParams: SearchQueryParams) => {
        const params = {
          ...queryParams,
          userId: this.userId,
        };

        if (params.organization_id) {
          this.isLoadingOrganizationEvents = true;
        }

        const observable = this.eventsHttpService.getEvents(params);

        observable.subscribe(() => {
          this.isLoadingOrganizationEvents = false;
        });

        return observable;
      }),
      tap((events) => {
        const targetEvent =
          events.find(
            (event) =>
              event.event_id ===
                this.localStorage.getInfo()?.activeResultsEvent ?? null
          ) ?? null;
        if (targetEvent) {
          this.selectEvent(targetEvent);
        } else {
          this.selectEvent(events[0]);
        }
      }),
      startWith(null)
    );

  @HostListener('window:resize')
  onResize(): void {
    this.innerWidth = window.innerWidth;
  }

  get showActiveEventInline(): boolean {
    return this.innerWidth < BREAKPOINT_MD;
  }

  ngOnInit(): void {
    this.authService.user$.subscribe((user) => {
      this.userId = user?.id;
    });

    this.organizations = this.route.snapshot.data['organizations']
      .data as Organization[];
  }

  public organisationId$: Observable<number | null> =
    this.route.queryParams.pipe(
      map((data) => Number(data['organization_id']) ?? null)
    );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsHttpService: EventsHttpService,
    private localStorage: LocalStorageService,
    private resizeService: ResizeService,
    public authService: AuthService,
    public cdr: ChangeDetectorRef
  ) {}

  selectEvent(event: EventResponseEvent): void {
    this.selectedEvent = event;
    if (event?.event_id) {
      this.localStorage.setInfo({ activeResultsEvent: event.event_id });
    }
  }

  getOrganisation(
    organisationId: number | undefined
  ): Organization | undefined {
    if (!organisationId) {
      return undefined;
    } else if (this.organization?.organization_id !== organisationId) {
      this.organization = this.organizations.find(
        (organisation) => organisation.organization_id === organisationId
      );
    }
    return this.organization;
  }

  removeOrganisation(): void {
    this.router.navigate([], {
      queryParams: {
        organization_id: null,
      },
      queryParamsHandling: 'merge',
    });
  }
}
